/**
 * Pure helpers for CRM labels (customers and leads). No React and no I/O so the rules the
 * picker and the chips share are unit-testable.
 */
import type { CSSProperties } from 'react';

/** Colours offered when a label is created in place. Stored as `#rrggbb`. */
export const LABEL_COLORS: readonly { readonly hex: string; readonly name: string }[] = [
  { hex: '#2563eb', name: 'آبی' },
  { hex: '#7c3aed', name: 'بنفش' },
  { hex: '#db2777', name: 'صورتی' },
  { hex: '#dc2626', name: 'قرمز' },
  { hex: '#d97706', name: 'نارنجی' },
  { hex: '#16a34a', name: 'سبز' },
  { hex: '#0d9488', name: 'فیروزه‌ای' },
  { hex: '#64748b', name: 'خاکستری' },
];

/** Same identity the server uses: case/space-insensitive, Arabic letters folded to Persian. */
export function labelKey(text: string): string {
  return text.replace(/ي/g, 'ی').replace(/ك/g, 'ک').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Name typed by a person → what is sent to the server (`''` when blank). */
export function cleanLabelText(text: string): string {
  return text.replace(/ي/g, 'ی').replace(/ك/g, 'ک').trim().replace(/\s+/g, ' ');
}

/**
 * The next palette colour for a new label, so the first labels of a tenant look different from
 * each other without the user having to choose.
 */
export function suggestLabelColor(existingColors: readonly (string | null)[]): string {
  const used = new Set(
    existingColors.filter((c): c is string => c !== null).map((c) => c.toLowerCase()),
  );
  return (LABEL_COLORS.find((c) => !used.has(c.hex)) ?? LABEL_COLORS[0]!).hex;
}

/** CSS custom property the `.crm-label` class tints itself from. Null → neutral grey. */
export function labelChipStyle(color: string | null): CSSProperties {
  return { ['--crm-label' as string]: color ?? '#64748b' } as CSSProperties;
}
